import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * RecoveryTaskReviewPage - BCM Manager Review of Team Recovery Plans
 * Allows BCM Manager to approve, reject, or request revisions for dependency recovery tasks
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ClipboardCheck,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Server,
  Building2,
  Truck,
  Cloud,
  Target,
  Eye,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Users,
  FileText,
  TrendingUp,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  revision_required: "bg-sky-100 text-sky-700",
};

const TEAM_ICONS = {
  it_team: Server,
  devops_team: Cloud,
  facility_team: Building2,
  vendor_team: Truck,
  business_process_owner: ClipboardCheck,
};

const TEAM_NAMES = {
  it_team: "IT Team (IT-DRP)",
  devops_team: "DevOps Team (IT-DRP)",
  facility_team: "Facility Team",
  vendor_team: "Vendor Team",
  business_process_owner: "Process Owner (BCP)",
};

const TEAM_COLORS = {
  it_team: "bg-blue-100",
  devops_team: "bg-cyan-100",
  facility_team: "bg-purple-100",
  vendor_team: "bg-sky-100",
  business_process_owner: "bg-emerald-100",
};

const PLAN_TYPES = {
  it_drp: { name: "IT-DRP", color: "bg-blue-100 text-blue-700", description: "IT Disaster Recovery Plan" },
  bcp: { name: "BCP", color: "bg-emerald-100 text-emerald-700", description: "Business Continuity Plan" },
};

const STRATEGY_NAMES = {
  hot_standby: "Hot Standby",
  warm_standby: "Warm Standby",
  cold_standby: "Cold Standby",
  manual_workaround: "Manual Workaround",
};

export default function RecoveryTaskReviewPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewComments, setReviewComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [filterTeam, setFilterTeam] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeTab, setActiveTab] = useState("pending");

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, statsRes] = await Promise.all([
        api.get("/recovery-tasks/all"),
        api.get("/recovery-tasks/stats/summary"),
      ]);
      setTasks(tasksRes.data.tasks || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load recovery tasks");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const openReviewDialog = (task) => {
    setSelectedTask(task);
    setReviewComments("");
    setShowReviewDialog(true);
  };

  const handleReview = async (action) => {
    if (!selectedTask) return;
    
    setSubmitting(true);
    try {
      await api.post(`/recovery-tasks/${selectedTask.id}/review`, {
        action,
        comments: reviewComments,
      });
      
      toast.success(`Task ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "returned for revision"}`);
      setShowReviewDialog(false);
      fetchTasks();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to submit review"));
    } finally {
      setSubmitting(false);
    }
  };

  const getTeamIcon = (teamType) => {
    const Icon = TEAM_ICONS[teamType] || ClipboardCheck;
    return <Icon className="w-5 h-5" />;
  };

  const getStatusBadge = (status) => {
    return (
      <Badge className={STATUS_COLORS[status] || "bg-slate-100"}>
        {status?.replace("_", " ").toUpperCase()}
      </Badge>
    );
  };

  const filteredTasks = tasks.filter(task => {
    // Filter by tab (status)
    if (activeTab === "pending" && task.status !== "submitted") return false;
    if (activeTab === "approved" && task.status !== "approved") return false;
    if (activeTab === "other" && ["submitted", "approved"].includes(task.status)) return false;
    
    // Filter by team
    if (filterTeam !== "all" && task.team_type !== filterTeam) return false;
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recovery-task-review-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-sky-100 text-sky-700">DO Phase</Badge>
            <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">ISO 22301:8.3-8.4</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Recovery Task Review</h1>
          <p className="text-slate-600 mt-1">
            Review and monitor recovery plans submitted by IT, DevOps, Facility, and Vendor teams. Approvals are handled by designated plan approvers.
          </p>
        </div>
        <Button variant="outline" onClick={fetchTasks} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Tasks</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <ClipboardCheck className="w-8 h-8 text-sky-700 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.by_status?.submitted > 0 ? "border-amber-200" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Review</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.by_status?.submitted || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Approved</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.by_status?.approved || 0}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.overdue > 0 ? "border-red-200" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{stats.overdue || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completion</p>
                  <p className="text-2xl font-bold text-sky-700">{stats.completion_rate || 0}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-sky-700 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Breakdown */}
      {stats?.by_team && (
        <div className="grid md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Server className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">IT Team Tasks</p>
                  <p className="font-semibold">
                    {stats.by_team.it_team?.completed || 0} / {stats.by_team.it_team?.total || 0} completed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Cloud className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">DevOps Team Tasks</p>
                  <p className="font-semibold">
                    {stats.by_team.devops_team?.completed || 0} / {stats.by_team.devops_team?.total || 0} completed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Facility Team Tasks</p>
                  <p className="font-semibold">
                    {stats.by_team.facility_team?.completed || 0} / {stats.by_team.facility_team?.total || 0} completed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-sky-100 rounded-lg">
                  <Truck className="w-5 h-5 text-sky-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Vendor Tasks</p>
                  <p className="font-semibold">
                    {stats.by_team.vendor_team?.completed || 0} / {stats.by_team.vendor_team?.total || 0} completed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <ClipboardCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">BCP Tasks (Process Owners)</p>
                  <p className="font-semibold">
                    {stats.by_team.business_process_owner?.completed || 0} / {stats.by_team.business_process_owner?.total || 0} completed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tasks List */}
      <Card data-testid="tasks-list">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-slate-400" />
                Recovery Tasks
              </CardTitle>
              <CardDescription>
                Review recovery plans from dependent teams
              </CardDescription>
            </div>
            <Select value={filterTeam} onValueChange={setFilterTeam}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                <SelectItem value="it_team">IT Team</SelectItem>
                <SelectItem value="devops_team">DevOps Team</SelectItem>
                <SelectItem value="facility_team">Facility Team</SelectItem>
                <SelectItem value="vendor_team">Vendor Team</SelectItem>
                <SelectItem value="business_process_owner">Process Owner (BCP)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pending Review
                {stats?.by_status?.submitted > 0 && (
                  <Badge variant="secondary">{stats.by_status.submitted}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="approved" className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Approved
              </TabsTrigger>
              <TabsTrigger value="other" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Other
              </TabsTrigger>
            </TabsList>

            {/* Plan Type Summary */}
            {stats?.by_plan_type && (
              <div className="flex gap-4 mb-4">
                <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
                  <Badge className="bg-blue-100 text-blue-700">IT-DRP</Badge>
                  <span className="text-sm">{stats.by_plan_type.it_drp?.completed || 0}/{stats.by_plan_type.it_drp?.total || 0}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 rounded-full">
                  <Badge className="bg-emerald-100 text-emerald-700">BCP</Badge>
                  <span className="text-sm">{stats.by_plan_type.bcp?.completed || 0}/{stats.by_plan_type.bcp?.total || 0}</span>
                </div>
              </div>
            )}

            <TabsContent value={activeTab}>
              {filteredTasks.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">
                    {activeTab === "pending" ? "No tasks pending review" : "No tasks in this category"}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredTasks.map((task) => (
                    <div
                      key={task.id}
                      className={`p-4 border rounded-lg hover:shadow-md transition-shadow ${task.is_bcp ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-blue-500'}`}
                      data-testid={`task-${task.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${TEAM_COLORS[task.team_type] || "bg-slate-100"}`}>
                            {getTeamIcon(task.team_type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{task.title}</h3>
                              {getStatusBadge(task.status)}
                              {task.is_cloud_hosted && (
                                <Badge className="bg-cyan-100 text-cyan-700">
                                  <Cloud className="w-3 h-3 mr-1" />
                                  {task.cloud_provider?.toUpperCase() || "Cloud"}
                                </Badge>
                              )}
                              {task.rto_warning && (
                                <Badge variant="destructive" className="text-xs">RTO Warning</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              Parent: {task.parent_process?.name} | Team: {TEAM_NAMES[task.team_type] || task.team_type}
                            </p>
                            
                            {task.recovery_plan && (
                              <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Target className="w-3 h-3" />
                                  Strategy: {STRATEGY_NAMES[task.recovery_plan.recovery_strategy?.strategy_type]}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Planned RTO: {task.recovery_plan.recovery_strategy?.planned_rto_hours}h
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {task.recovery_plan.recovery_steps?.length || 0} steps
                                </span>
                              </div>
                            )}
                            
                            {task.submitted_by && (
                              <p className="text-xs text-slate-400 mt-2">
                                Submitted by {task.submitted_by} on {new Date(task.submitted_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openReviewDialog(task)}
                            data-testid={`review-${task.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTask && getTeamIcon(selectedTask.team_type)}
              Review: {selectedTask?.title}
            </DialogTitle>
            <DialogDescription>
              {TEAM_NAMES[selectedTask?.team_type]} recovery plan for {selectedTask?.parent_process?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedTask && (
            <div className="space-y-4">
              {/* RTO Comparison */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-500">Parent RTO</p>
                  <p className="text-xl font-bold">{selectedTask.parent_process?.rto_hours}h</p>
                </div>
                <div className="p-3 bg-sky-50 rounded-lg text-center">
                  <p className="text-xs text-sky-700">Team Target RTO</p>
                  <p className="text-xl font-bold text-sky-700">
                    {selectedTask.team_bia_reference?.rto_hours || "N/A"}h
                  </p>
                </div>
                <div className={`p-3 rounded-lg text-center ${
                  selectedTask.recovery_plan?.recovery_strategy?.planned_rto_hours > (selectedTask.team_bia_reference?.rto_hours || 999)
                    ? "bg-red-50"
                    : "bg-emerald-50"
                }`}>
                  <p className="text-xs text-slate-500">Planned RTO</p>
                  <p className={`text-xl font-bold ${
                    selectedTask.recovery_plan?.recovery_strategy?.planned_rto_hours > (selectedTask.team_bia_reference?.rto_hours || 999)
                      ? "text-red-700"
                      : "text-emerald-700"
                  }`}>
                    {selectedTask.recovery_plan?.recovery_strategy?.planned_rto_hours || "N/A"}h
                  </p>
                </div>
              </div>

              {selectedTask.rto_warning && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <p className="text-sm text-amber-800">{selectedTask.rto_warning}</p>
                  </div>
                </div>
              )}

              {/* Recovery Strategy */}
              {selectedTask.recovery_plan && (
                <>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-2">Recovery Strategy</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p>Type: <strong>{STRATEGY_NAMES[selectedTask.recovery_plan.recovery_strategy?.strategy_type]}</strong></p>
                      <p>Cost: {selectedTask.recovery_plan.recovery_strategy?.cost_estimate}</p>
                    </div>
                    {selectedTask.recovery_plan.recovery_strategy?.justification && (
                      <p className="text-sm text-slate-600 mt-2">
                        Justification: {selectedTask.recovery_plan.recovery_strategy.justification}
                      </p>
                    )}
                  </div>

                  {/* Recovery Procedures — Always renders in template table format */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    {(() => {
                      const tasks = selectedTask.recovery_plan.recovery_tasks?.length > 0
                        ? selectedTask.recovery_plan.recovery_tasks
                        : (selectedTask.recovery_plan.recovery_steps || []).map((step, idx) => ({
                            task_id: `T${String(idx + 1).padStart(3, '0')}`,
                            actual_task: step.action,
                            task_owner: step.responsible || "—",
                            predecessor_task_id: idx > 0 ? `T${String(idx).padStart(3, '0')}` : null,
                            estimated_duration: `${step.duration_mins} mins`,
                            estimated_duration_mins: step.duration_mins,
                            start_time: null,
                            completion_time: null,
                            notes: step.notes || "",
                          }));
                      return tasks.length > 0 ? (
                        <>
                          <h4 className="font-medium mb-3">Recovery Plan — Task Table ({tasks.length} tasks)</h4>
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-slate-800">
                                  <TableHead className="text-white font-semibold w-16 text-xs">Task ID</TableHead>
                                  <TableHead className="text-white font-semibold text-xs">Actual Task</TableHead>
                                  <TableHead className="text-white font-semibold w-28 text-xs">Task Owner</TableHead>
                                  <TableHead className="text-white font-semibold w-24 text-xs">Predecessor</TableHead>
                                  <TableHead className="text-white font-semibold w-24 text-xs">Est. Duration</TableHead>
                                  <TableHead className="text-white font-semibold w-20 text-xs">Start</TableHead>
                                  <TableHead className="text-white font-semibold w-24 text-xs">Completion</TableHead>
                                  <TableHead className="text-white font-semibold w-28 text-xs">Notes</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {tasks.map((task, i) => (
                                  <TableRow key={i} className="text-sm" data-testid={`review-task-row-${i}`}>
                                    <TableCell className="font-mono font-semibold text-purple-700 text-xs">{task.task_id}</TableCell>
                                    <TableCell className="font-medium text-xs">{task.actual_task}</TableCell>
                                    <TableCell className="text-xs">{task.task_owner}</TableCell>
                                    <TableCell className="font-mono text-slate-600 text-xs">{task.predecessor_task_id || "—"}</TableCell>
                                    <TableCell className="text-xs">{task.estimated_duration}</TableCell>
                                    <TableCell className="text-xs text-slate-400">{task.start_time || "—"}</TableCell>
                                    <TableCell className="text-xs text-slate-400">{task.completion_time || "—"}</TableCell>
                                    <TableCell className="text-xs text-slate-500 max-w-[100px] truncate">{task.notes || "—"}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          <p className="text-sm text-slate-500 mt-2">
                            Total estimated time: {tasks.reduce((sum, t) => sum + (t.estimated_duration_mins || 0), 0)} minutes
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-slate-500 italic">No recovery procedures defined.</p>
                      );
                    })()}
                  </div>

                  {/* Validation Steps */}
                  {selectedTask.recovery_plan.validation_steps?.length > 0 && (
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <h4 className="font-medium mb-2 text-emerald-800">Validation Checklist</h4>
                      <ul className="text-sm space-y-1 text-emerald-700">
                        {selectedTask.recovery_plan.validation_steps.map((step, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3" />
                            {step}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Prerequisites & Resources */}
                  {(selectedTask.recovery_plan.prerequisites || selectedTask.recovery_plan.resource_requirements) && (
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTask.recovery_plan.prerequisites && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <h5 className="text-sm font-medium mb-1">Prerequisites</h5>
                          <p className="text-sm text-slate-600">{selectedTask.recovery_plan.prerequisites}</p>
                        </div>
                      )}
                      {selectedTask.recovery_plan.resource_requirements && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <h5 className="text-sm font-medium mb-1">Resource Requirements</h5>
                          <p className="text-sm text-slate-600">{selectedTask.recovery_plan.resource_requirements}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Submission Notes */}
                  {selectedTask.submission_comments && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <h5 className="text-sm font-medium mb-1 text-blue-800">Submitter Comments</h5>
                      <p className="text-sm text-blue-700">{selectedTask.submission_comments}</p>
                    </div>
                  )}
                </>
              )}

              {/* Approver Info */}
              {selectedTask.approver?.name && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-xs text-purple-600 mb-1">Designated Approver</p>
                  <p className="font-medium text-sm text-purple-800">{selectedTask.approver.name} ({selectedTask.approver.email})</p>
                </div>
              )}

              {/* Previous Review Comments */}
              {selectedTask.review_comments && selectedTask.status !== "submitted" && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h5 className="text-sm font-medium mb-1">Review Comments</h5>
                  <p className="text-sm text-slate-600">{selectedTask.review_comments}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    By {selectedTask.reviewed_by} on {new Date(selectedTask.reviewed_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex justify-between">
            {selectedTask?.approver?.name && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mr-auto">
                <Badge variant="outline" className="text-purple-600 border-purple-200">Approver: {selectedTask.approver.name}</Badge>
              </div>
            )}
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
